import type { CountryFeature, CountryFeatureCollection } from './types'
import { COUNTRIES_URL, state } from './state'
import { t } from './i18n'

/** Fetches the GeoJSON region dataset into `state.countries`. Throws on failure. */
export async function loadCountries(): Promise<void> {
  const response = await fetch(COUNTRIES_URL)
  if (!response.ok) throw new Error(t('couldNotLoadCountries'))

  const geoJson = (await response.json()) as CountryFeatureCollection
  state.countries = geoJson.features
}

export function findCountryAtLatLng(latlng: L.LatLng): string {
  const point = turfPoint([latlng.lng, latlng.lat])
  const feature = state.countries.find((c) => pointInFeature(point, c))
  return feature?.properties?.name ?? ''
}

export function isLatLngInsideSelectedCountry(latlng: L.LatLng): boolean {
  if (!state.selectedCountry) return false
  const point = turfPoint([latlng.lng, latlng.lat])
  const feature = state.countries.find(
    (c) => c.properties.name === state.selectedCountry,
  )
  return feature ? pointInFeature(point, feature) : false
}

export function findCountryFeature(name: string): CountryFeature | undefined {
  return state.countries.find((c) => c.properties.name === name)
}

// ---- point-in-polygon (handles MultiPolygon + holes) ------------------------

interface TurfPoint {
  type: 'Point'
  coordinates: number[]
}

function turfPoint(coordinates: number[]): TurfPoint {
  return { type: 'Point', coordinates }
}

function pointInFeature(point: TurfPoint, feature: CountryFeature): boolean {
  const g = feature.geometry
  if (g.type === 'Polygon') {
    return pointInPolygon(point.coordinates, g.coordinates as number[][][])
  }
  if (g.type === 'MultiPolygon') {
    return (g.coordinates as number[][][][]).some((polygon) =>
      pointInPolygon(point.coordinates, polygon),
    )
  }
  return false
}

function pointInPolygon(point: number[], polygon: number[][][]): boolean {
  const [outerRing, ...holes] = polygon
  if (!outerRing || !pointInRing(point, outerRing)) return false
  return !holes.some((ring) => pointInRing(point, ring))
}

function pointInRing(point: number[], ring: number[][]): boolean {
  let inside = false
  const [x, y] = point
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const [xi, yi] = ring[i]
    const [xj, yj] = ring[j]
    const intersect =
      yi > y !== yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi
    if (intersect) inside = !inside
  }
  return inside
}
